import { useApiScope, type ApiScope } from "@/features/utils/api-scope";
import { useGlobalState } from "@/features/utils/use-global-state";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DevicesAPI } from "../api/devices";
import type {
  AssignableDevice,
  Device,
  DeviceBase,
  DeviceBaseHistoryEntry,
  DeviceBaseInput,
  DeviceCall,
  DeviceCredentials,
  DeviceInput,
  SipConnection,
} from "../types";

/**
 * Cache keys, every one of them scoped.
 *
 * The scope is part of the key because the same handset is reachable through
 * two mounts, and a SYSADMIN looking at their *own* tenant would otherwise have
 * the admin view and the back-office share one cache entry — the second of the
 * two to load would decide what both show.
 */
export const deviceKeys = {
  all: ["sessions_devices"] as const,
  scope: (scope: ApiScope) => [...deviceKeys.all, scope] as const,
  lists: () => [...deviceKeys.all, "list"] as const,
  list: (
    scope: ApiScope,
    clientId: string,
    filters?: { connector_id?: string },
  ) => [...deviceKeys.lists(), scope, clientId, filters] as const,
  assignable: (clientId: string) =>
    [...deviceKeys.all, "assignable", clientId] as const,
  calls: (scope: ApiScope, clientId: string, deviceId: string) =>
    [...deviceKeys.all, "calls", scope, clientId, deviceId] as const,
  details: () => [...deviceKeys.all, "detail"] as const,
  detail: (scope: ApiScope, clientId: string, deviceId: string) =>
    [...deviceKeys.details(), scope, clientId, deviceId] as const,
  bases: (scope: ApiScope, clientId: string) =>
    [...deviceKeys.all, "bases", scope, clientId] as const,
  baseHistory: (scope: ApiScope, clientId: string, baseId: string) =>
    [...deviceKeys.all, "base-history", scope, clientId, baseId] as const,
};

/**
 * Handsets a member can point their profile at. Unlike {@link useDevices} this
 * is readable by every member, so it backs the picker in the member profile
 * dialog rather than the provisioning table.
 */
export function useAssignableDevices(clientId: string, enabled = true) {
  return useQuery<AssignableDevice[]>({
    queryKey: deviceKeys.assignable(clientId),
    queryFn: () => DevicesAPI.getAssignableDevices(clientId),
    enabled: !!clientId && enabled,
    staleTime: 30 * 1000,
  });
}

/**
 * Handsets, with their live registration state.
 *
 * Refetched on an interval because the interesting column — is the phone
 * plugged in? — changes without anything happening in this browser tab, and
 * the whole point of the page is watching a phone come online after someone
 * types the credentials into it.
 */
export function useDevices(
  clientId: string,
  filters?: { connector_id?: string },
) {
  const scope = useApiScope();

  return useQuery<Device[]>({
    queryKey: deviceKeys.list(scope, clientId, filters),
    queryFn: () => DevicesAPI.getDevices(scope, clientId, filters),
    enabled: !!clientId,
    staleTime: 5 * 1000,
    refetchInterval: 15 * 1000,
  });
}

export function useDevice(clientId: string, deviceId: string) {
  const scope = useApiScope();

  return useQuery<Device>({
    queryKey: deviceKeys.detail(scope, clientId, deviceId),
    queryFn: () => DevicesAPI.getDevice(scope, clientId, deviceId),
    enabled: !!clientId && !!deviceId,
    staleTime: 30 * 1000,
  });
}

export function useCreateDevice() {
  const queryClient = useQueryClient();
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: DeviceInput;
    }) => DevicesAPI.createDevice(scope, clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
}

export function useUpdateDevice() {
  const queryClient = useQueryClient();
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({
      clientId,
      deviceId,
      data,
    }: {
      clientId: string;
      deviceId: string;
      data: DeviceInput;
    }) => DevicesAPI.updateDevice(scope, clientId, deviceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
    },
  });
}

export function useDeleteDevice() {
  const queryClient = useQueryClient();
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({
      clientId,
      deviceId,
    }: {
      clientId: string;
      deviceId: string;
    }) => DevicesAPI.deleteDevice(scope, clientId, deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
}

/**
 * Deliberately does **not** invalidate on success.
 *
 * The new password exists only in this response, and it is displayed by a
 * dialog whose open state lives in the table row's component. Invalidating here
 * refetches the list, re-renders the table with a fresh `data` array, and the
 * row that was holding the credentials can lose its state — the dialog opens
 * and shuts immediately. The caller invalidates once the dialog is dismissed,
 * which is also when the refreshed row is actually worth looking at.
 */
export function useRegenerateDeviceCredentials() {
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({
      clientId,
      deviceId,
    }: {
      clientId: string;
      deviceId: string;
    }) => DevicesAPI.regenerateCredentials(scope, clientId, deviceId),
  });
}

/**
 * No cache invalidation: ringing a phone changes nothing about the row, and a
 * refetch here would only make the button feel slower than the phone.
 */
export function useRingDevice() {
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({
      clientId,
      deviceId,
    }: {
      clientId: string;
      deviceId: string;
    }) => DevicesAPI.ringDevice(scope, clientId, deviceId),
  });
}

/**
 * The freshly issued credentials awaiting display, held **globally** rather
 * than in the component that produced them.
 *
 * They used to live in `useState` inside the table row's action component, and
 * the dialog would appear and vanish about a second later. Anything that
 * re-renders the table with a new `data` array — a refetch, the polling
 * interval, an invalidation elsewhere — can have React reconcile that row into
 * a fresh component instance, taking its state with it. The dialog holds the
 * only copy of a password that exists nowhere else, so losing it costs the user
 * a reissue.
 *
 * Lifting it to page level makes the table's render cycle irrelevant: the
 * dialog is mounted once, by a component the table cannot unmount.
 */
export type CredentialsPrompt = {
  /** Which handset, so the dialog can offer to re-issue for it. */
  deviceId: string;
  connection: SipConnection;
  /**
   * Present only when credentials have just been issued. Absent means the
   * dialog was opened to read the settings, and all there is to show in its
   * place is {@link passwordHint} — the password itself was never stored.
   */
  password?: string;
  /**
   * The first two characters of the handset's password, kept on its row. Shown
   * when `password` is absent, which is every time but the one.
   */
  passwordHint?: string;
};

export function useDeviceCredentialsPrompt() {
  const [prompt, setPrompt] = useGlobalState<CredentialsPrompt | null>(
    "devices:credentials",
    null,
  );

  return {
    prompt: prompt ?? null,
    /** Just issued: carries the one copy of the password there will ever be. */
    showIssued: (deviceId: string, credentials: DeviceCredentials) =>
      setPrompt({
        deviceId,
        connection: credentials,
        password: credentials.password,
      }),
    /**
     * Opened from the list: the same screen, minus the secret — the hint
     * standing in for it, because "which password is on that phone" is what
     * this dialog is opened for.
     */
    showConnection: (
      deviceId: string,
      connection: SipConnection,
      passwordHint?: string,
    ) => setPrompt({ deviceId, connection, passwordHint }),
    clear: () => setPrompt(null),
  };
}

/**
 * What a handset has been dialling, refusals included.
 *
 * Only fetched while the history is open — it is a hundred rows nobody is
 * looking at the rest of the time — and refreshed on the same cadence as the
 * list, so a call placed while the panel is open appears without a reload.
 */
export function useDeviceCalls(
  clientId: string,
  deviceId: string,
  enabled = true,
) {
  const scope = useApiScope();

  return useQuery<DeviceCall[]>({
    queryKey: deviceKeys.calls(scope, clientId, deviceId),
    queryFn: () => DevicesAPI.getDeviceCalls(scope, clientId, deviceId),
    enabled: !!clientId && !!deviceId && enabled,
    staleTime: 10 * 1000,
    refetchInterval: 20 * 1000,
  });
}

/**
 * Every base of the tenant, with the handsets that run on each.
 *
 * Not on the handset list's polling interval: nothing here changes on its own
 * the way a registration does — it changes when somebody in this browser edits
 * it, and the mutations below invalidate.
 */
export function useDeviceBases(clientId: string) {
  const scope = useApiScope();

  const query = useQuery<DeviceBase[]>({
    queryKey: deviceKeys.bases(scope, clientId),
    queryFn: () => DevicesAPI.getBases(scope, clientId),
    enabled: !!clientId,
    staleTime: 60 * 1000,
  });

  return {
    ...query,
    byId: new Map((query.data || []).map((base) => [base.id!, base])),
    /**
     * Base of a given handset, which is how the handset table shows a column of
     * them without a second request.
     */
    byDevice: new Map(
      (query.data || []).flatMap((base) =>
        base.devices.map((device) => [device.id, base] as const),
      ),
    ),
  };
}

export function useCreateDeviceBase() {
  const queryClient = useQueryClient();
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: DeviceBaseInput;
    }) => DevicesAPI.createBase(scope, clientId, data),
    onSuccess: (_result, { clientId }) => {
      queryClient.invalidateQueries({
        queryKey: deviceKeys.bases(scope, clientId),
      });
    },
  });
}

export function useUpdateDeviceBase() {
  const queryClient = useQueryClient();
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({
      clientId,
      baseId,
      data,
    }: {
      clientId: string;
      baseId: string;
      data: DeviceBaseInput;
    }) => DevicesAPI.updateBase(scope, clientId, baseId, data),
    onSuccess: (_result, { clientId, baseId }) => {
      queryClient.invalidateQueries({
        queryKey: deviceKeys.bases(scope, clientId),
      });
      // The trail gained an entry, and a staff member looking at it while the
      // owner edits should see it appear rather than a stale list.
      queryClient.invalidateQueries({
        queryKey: deviceKeys.baseHistory(scope, clientId, baseId),
      });
    },
  });
}

/**
 * Deleting a base detaches its handsets, so the handset list is stale too — it
 * shows a base column.
 */
export function useDeleteDeviceBase() {
  const queryClient = useQueryClient();
  const scope = useApiScope();

  return useMutation({
    mutationFn: ({ clientId, baseId }: { clientId: string; baseId: string }) =>
      DevicesAPI.deleteBase(scope, clientId, baseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
    },
  });
}

/**
 * Every value a base has ever held. SYSADMIN only — the route answers 403 to
 * anybody else, so it is fetched solely when the panel is open *and* the viewer
 * is staff.
 */
export function useDeviceBaseHistory(
  clientId: string,
  baseId: string,
  enabled = true,
) {
  const scope = useApiScope();

  return useQuery<DeviceBaseHistoryEntry[]>({
    queryKey: deviceKeys.baseHistory(scope, clientId, baseId),
    queryFn: () => DevicesAPI.getBaseHistory(scope, clientId, baseId),
    enabled: !!clientId && !!baseId && enabled,
    staleTime: 30 * 1000,
  });
}

/**
 * Which base is open, held globally rather than in the row.
 *
 * Same reason as {@link useDeviceCallsPrompt}: the lists refetch on an interval,
 * and a dialog whose open state lives in a table cell is dismissed the moment
 * React reconciles that row — here in the middle of typing a password.
 *
 * The id rather than the row: a base is edited from two tables (its own, and
 * the handset list's base column), and holding the row would have the dialog
 * showing values the other table has since refetched.
 */
export function useDeviceBasePrompt() {
  const [baseId, setBaseId] = useGlobalState<string | null>(
    "devices:base",
    null,
  );
  return {
    baseId: baseId ?? null,
    show: (value: string) => setBaseId(value),
    /** Opens the dialog on a base that does not exist yet. */
    showNew: () => setBaseId(NEW_BASE),
    clear: () => setBaseId(null),
  };
}

/** What {@link useDeviceBasePrompt} holds while a base is being created. */
export const NEW_BASE = "new";

/**
 * Which handset's history is open, held globally rather than in the row.
 *
 * Same reason as {@link useDeviceCredentialsPrompt}: the list refetches on an
 * interval, and state that lives in a table cell does not reliably survive the
 * row being reconciled. Losing this one only costs a click rather than a
 * password, but the fix is free and the failure is the same shape.
 */
export function useDeviceCallsPrompt() {
  const [device, setDevice] = useGlobalState<Device | null>(
    "devices:calls",
    null,
  );
  return {
    device: device ?? null,
    show: (value: Device) => setDevice(value),
    clear: () => setDevice(null),
  };
}
